import React from 'react';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Column, Table } from '@tanstack/react-table';
import PropTypes from 'prop-types';

import ColumnOption from './ColumnOption';
import DragHandle from './DragHandle';
import { Themeable } from '../../types';

export type DnDColumnsProps<D extends Record<string, unknown>> = Themeable & {
  columns: Column<D>[];
  setColumnOrder: Table<D>['setColumnOrder'];
  getColumnName?: (column: Column<D>) => React.ReactNode;
};

/**
 * Drag N Drop Columns
 *
 * A convenience component for reordering columns in the table configuration menu.
 * If any customization is needed for a use case, please copy the markup into that context and do not extend or override or create any branching within this component. All of the children used here are exported from Table and should be configured directly when customization is needed.
 */
const DnDColumns = <D extends Record<string, unknown>>({
  columns,
  setColumnOrder,
  getColumnName,
}: DnDColumnsProps<D>) => {
  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (result.destination?.index === undefined) {
          return;
        }
        const reorderedColumns = Array.from(columns.map((c) => c.id));
        const [reorderedItem] = reorderedColumns.splice(result.source.index, 1);
        reorderedColumns.splice(result.destination.index, 0, reorderedItem);
        setColumnOrder(reorderedColumns);
      }}
    >
      <Droppable
        droppableId="columns"
        renderClone={(provided, _, rubric) => (
          <ColumnOption
            ref={provided.innerRef}
            column={columns[rubric.source.index]}
            {...provided.draggableProps}
            getColumnName={getColumnName}
          >
            <DragHandle {...provided.dragHandleProps} />
          </ColumnOption>
        )}
      >
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {columns.map((column, index) => {
              return (
                <Draggable
                  key={column.id}
                  draggableId={column.id}
                  index={index}
                >
                  {(provided) => (
                    <ColumnOption
                      ref={provided.innerRef}
                      column={column}
                      {...provided.draggableProps}
                      getColumnName={getColumnName}
                    >
                      <DragHandle {...provided.dragHandleProps} />
                    </ColumnOption>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

DnDColumns.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setColumnOrder: PropTypes.func.isRequired,
  getColumnName: PropTypes.func,
};

export default DnDColumns;
